import React from 'react';
import { ProgressBar } from './ProgressBar';
import Card from '../../../components/base/Card';
import Button from '../../../components/base/Button';
import { Edit, Trash2, AlertTriangle, TrendingUp, TrendingDown } from 'lucide-react';
import type { EstadoPresupuesto } from '../../../shared/types/presupuestos';

interface ListaPresupuestosProps {
  estados: EstadoPresupuesto[];
  onEditar: (presupuestoId: string) => void;
  onEliminar: (presupuestoId: string) => void;
  className?: string;
}

export const ListaPresupuestos: React.FC<ListaPresupuestosProps> = ({
  estados,
  onEditar,
  onEliminar,
  className = ''
}) => {
  const formatearMoneda = (monto: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(monto);
  };

  const formatearPeriodo = (periodo: string) => {
    const periodos: Record<string, string> = {
      'mensual': 'Mensual',
      'semanal': 'Semanal',
      'anual': 'Anual'
    };
    return periodos[periodo] || periodo;
  };

  const getStatusIcon = (estado: EstadoPresupuesto['estado']) => {
    switch (estado) {
      case 'superado':
        return <TrendingDown className="w-5 h-5 text-red-500" />;
      case 'advertencia':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      default:
        return <TrendingUp className="w-5 h-5 text-green-500" />;
    }
  };

  const getStatusColor = (estado: EstadoPresupuesto['estado']) => {
    switch (estado) {
      case 'superado':
        return 'text-red-700 bg-red-50 border-red-200';
      case 'advertencia':
        return 'text-yellow-700 bg-yellow-50 border-yellow-200';
      default:
        return 'text-green-700 bg-green-50 border-green-200';
    }
  };

  const getStatusLabel = (estado: EstadoPresupuesto['estado']) => {
    switch (estado) {
      case 'superado':
        return 'Superado';
      case 'advertencia':
        return 'Advertencia';
      default:
        return 'Normal';
    }
  };

  if (estados.length === 0) {
    return (
      <Card className={`p-8 text-center ${className}`}>
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <TrendingUp className="w-8 h-8 text-gray-400" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          No hay presupuestos configurados
        </h3>
        <p className="text-gray-600">
          Crea tu primer presupuesto para comenzar a controlar tus gastos por categoría.
        </p>
      </Card>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {estados.map((estado) => (
        <Card key={estado.presupuesto.id} className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-start space-x-3">
              {getStatusIcon(estado.estado)}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                  {estado.presupuesto.categoria}
                </h3>
                <p className="text-sm text-gray-600">
                  {formatearPeriodo(estado.presupuesto.periodo)}
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(estado.estado)}`}>
                {getStatusLabel(estado.estado)}
              </span>
              
              <div className="flex space-x-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onEditar(estado.presupuesto.id)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <Edit className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onEliminar(estado.presupuesto.id)}
                  className="text-gray-500 hover:text-red-600"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Información del presupuesto */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <p className="text-sm text-gray-600 mb-1">Presupuestado</p>
              <p className="text-lg font-semibold text-gray-900">
                {formatearMoneda(estado.presupuesto.monto)}
              </p>
            </div>
            
            <div>
              <p className="text-sm text-gray-600 mb-1">Gastado</p>
              <p className="text-lg font-semibold text-gray-900">
                {formatearMoneda(estado.gastoActual)}
              </p>
            </div>
            
            <div>
              <p className="text-sm text-gray-600 mb-1">Disponible</p>
              <p className={`text-lg font-semibold ${estado.disponible >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatearMoneda(estado.disponible)}
              </p>
            </div>
          </div>

          {/* Barra de progreso */}
          <ProgressBar
            porcentaje={estado.porcentajeUsado}
            estado={estado.estado}
            showLabel={true}
            className="mb-4"
          />

          {/* Descripción si existe */}
          {estado.presupuesto.descripcion && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                {estado.presupuesto.descripcion}
              </p>
            </div>
          )}

          {/* Fechas del período */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex justify-between text-xs text-gray-500">
              <span>
                Período: {new Intl.DateTimeFormat('es-ES', {
                  day: 'numeric',
                  month: 'short'
                }).format(estado.presupuesto.fechaInicio)} - {new Intl.DateTimeFormat('es-ES', {
                  day: 'numeric',
                  month: 'short'
                }).format(estado.presupuesto.fechaFin)}
              </span>
              <span>
                Actualizado: {new Intl.DateTimeFormat('es-ES', {
                  day: 'numeric',
                  month: 'short',
                  hour: '2-digit',
                  minute: '2-digit'
                }).format(estado.presupuesto.fechaActualizacion)}
              </span>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
};